import { fetcher } from 'utils/axios';
import { getChatMessages } from 'api/chat';
import { BulkChatUsersActionsParams } from 'types/chat/api-types';
import { CompanyPhoneConfig } from 'types/company-platforms/companyPhoneConfig';
import { ChatUserModel } from 'types/chat-users/chat-user';
import { MessageModel } from 'types/chat/messages/message';
import { ChatUserConfigType } from 'types/chat/props';
import { DEFAULT_MARKETING_CONSENT_LEVEL } from 'views/contacts/chat/customer-messaging-status';

export const getChatUser = async (id: number) => {
  try {
    const data = await fetcher(
      'post',
      [
        '/contacts/getLegacy',
        {
          data: {
            id,
          },
        },
      ],
      'api',
    );

    if (data?.result !== false) {
      return data.contact as ChatUserModel;
    } else return null;
  } catch (error) {
    console.warn('getChatUser error: ', error);
    return null;
  }
};
export const getChatUserByPhoneNumber = async (phoneNumber: string) => {
  try {
    const data = await fetcher(
      'post',
      [
        '/contacts/getLegasyByPhone',
        {
          data: {
            phoneNumber,
          },
        },
      ],
      'api',
    );

    if (data) {
      return data as {
        contact: Array<ChatUserModel>;
        lastIncomingMessage: MessageModel | null;
        lastMessage: MessageModel | null;
      };
    } else return null;
  } catch (error) {
    console.warn('getChatUser error: ', error);
    return null;
  }
};

export const createChatUser = async (
  firstName: string,
  lastName: string,
  phone: string,
  email: string,
  companyPhoneConfig?: CompanyPhoneConfig,
  options?: { utmSource?: string },
) => {
  try {
    const params = {
      firstName,
      lastName,
      phone,
      email,
      isFireberry: false,
      companyPhoneConfig,
      marketingConsentLevel: DEFAULT_MARKETING_CONSENT_LEVEL,
      ...(options?.utmSource != null && String(options.utmSource).trim() !== ''
        ? { utmSource: String(options.utmSource).trim() }
        : {}),
    };

    const data = await fetcher('post', ['/api/services/CreateChatUser', { data: params }], 'history');
    
    // Debug logging
    console.log('CreateChatUser API response:', data);
    console.log('Response type:', typeof data);
    console.log('Response keys:', data ? Object.keys(data) : 'null');
    
    return data as
      | ChatUserModel
      | {
          message: string;
        };
  } catch (error) {
    console.warn('createChatUser error: ', error);
    return null;
  }
};

export const fetchGetChatUserConfig = async (phone: string) => {
  try {
    const data = await fetcher('get', `/api/services/getChatUserConfig?chatUserPhoneId=${phone}`, 'history');
    if (data)
      return data as {
        result: boolean;
        configObj: string;
      };
    else return null;
  } catch (error) {
    console.warn('getChatUserConfig error: ', error);
    return null;
  }
};

//* Chat users bulk actions — שרת הנוד (bulkConversationActions).
//* מריץ לכל שיחה את אותה לוגיקה של הפעולה הבודדת (תגית שיחה / שיוך סשן / סגירה / נקרא),
//* בניגוד ל-endpoint הישן ב-server3 שכתב לשדות legacy ש-web3 לא קורא.
export type BulkConversationAction = 'setTag' | 'assign' | 'close' | 'setRead' | 'setUnread';

export type BulkConversationActionsResult = {
  action: BulkConversationAction;
  total: number;
  succeeded: number;
  failed: number;
  errors: Array<{ phone: string; message: string }>;
};

export const bulkConversationActions = async (params: {
  action: BulkConversationAction;
  phones: string[];
  tag?: string;
  managerId?: string | null;
}): Promise<BulkConversationActionsResult | null> => {
  try {
    const response = (await fetcher(
      'post',
      ['/api/services/bulkConversationActions', { data: params }],
      'servernode',
    )) as { success?: boolean; data?: BulkConversationActionsResult } | null;
    if (!response?.success || !response.data) return null;
    return response.data;
  } catch (error) {
    console.warn('bulkConversationActions error: ', error);
    return null;
  }
};

export const fetchMakeChatUserUnread = async (phone: string, _messageId?: string | number) => {
  // ההודעה הנכנסת האחרונה נפתרת בשרת (updateContactReadStatus עם read=false)
  return await bulkConversationActions({ action: 'setUnread', phones: [phone] });
};

/** «סמן כלא נקראה» — לפי ההודעה הנכנסת האחרונה (כמו בתפריט שורת שיחה). */
export async function markChatUserUnreadByPhone(
  phone: string,
  user?: Pick<ChatUserModel, 'incomingLastMessageId'> | null,
): Promise<void> {
  try {
    let lastIncomingMessageId: string | number | null = user?.incomingLastMessageId ?? null;
    if (!lastIncomingMessageId) {
      const messagesRes = await getChatMessages(1, phone, 50);
      const lastIncomingMessage = messagesRes?.messages?.find((msg) => msg.Type === 'incoming');
      lastIncomingMessageId = lastIncomingMessage?.Id ?? null;
    }
    if (lastIncomingMessageId) {
      await fetchMakeChatUserUnread(phone, lastIncomingMessageId);
    }
  } catch {
    /* ignore — כמו makeChatUserUnread בשורה */
  }
}
export const fetchMakeChatUserRead = async (id: number) => {
  try {
    await fetcher(
      'put',
      [
        '/api/v1/contacts/setRead',
        {
          data: {
            id,
          },
        },
      ],
      'history',
    );
  } catch (error) {
    console.warn('makeChatUserRead error', error);
  }
};
export const fetchUpdateChatUserIcon = async (phone: string) => {
  try {
    const data = await fetcher(
      'get',
      [
        '/api/services/getChatUserWhatsappUserPick',
        {
          data: {
            whatsappPhoneNumberId: phone,
          },
        },
      ],
      'history',
    );

    return data;
  } catch (error) {
    console.warn('updateChatUserIcon error: ', error);
    return null;
  }
};
export const fetchUpdateChatUserDontDisturb = async (phone: string, state: boolean) => {
  try {
    const data = await fetcher(
      'post',
      [
        '/api/services/SetChatUserDontDisturb',
        {
          data: {
            phone: phone,
            dontDisturb: state,
          },
        },
      ],
      'history',
    );
    return data;
  } catch (error) {
    console.warn('updateChatUserDontDisturb error: ', error);
    return null;
  }
};
export const fetchDeleteChatUsers = async (list: string[]) => {
  try {
    const data = await fetcher(
      'post',
      [
        '/api/services/deleteChatUser',
        {
          data: list,
        },
      ],
      'history',
    );
    return data;
  } catch (error) {
    console.warn('deleteChatUsers error: ', error);
    return null;
  }
};

//* update functions
export const updateChatUserRelationToCompanyCustomer = async ({
  OwnerCompanyCustomerId,
  ChatUserRole,
  ChatUserPhoneNumberId,
}: {
  OwnerCompanyCustomerId: number | null;
  ChatUserRole: string | null;
  ChatUserPhoneNumberId: string | null;
}) => {
  try {
    const data = await fetcher(
      'post',
      [
        '/api/companyCustomer/updateChatUserRelationToCompanyCustomer',
        {
          data: {
            OwnerCompanyCustomerId,
            ChatUserRole,
            ChatUserPhoneNumberId,
          },
        },
      ],
      'history',
    );
    if (data)
      return data as {
        result: boolean;
        OwnerCompanyCustomerId: number | null;
        ChatUserRole: string | null;
      };
    else return null;
  } catch (error) {
    console.warn('updateChatUserRelationToCompanyCustomer error: ', error);
    return null;
  }
};
export const fetchSaveChatUserConfig = async (chatUserPhoneId: string, configObject: ChatUserConfigType) => {
  try {
    const data = await fetcher(
      'post',
      [
        '/api/services/saveChatUserConfig',
        {
          data: {
            chatUserPhoneId,
            configJsonString: JSON.stringify(configObject),
          },
        },
      ],
      'history',
    );
    return data as {
      result: boolean;
      message: string;
    };
  } catch (error) {
    console.warn('saveChatUserConfig error: ', error);
    return null;
  }
};
